/*
 * Stores the current short-lived data-access token and renews it before expiration.
 */

// Renew early enough to let in-flight data requests finish with the previous token.
const RENEWAL_ADVANCE_MS = 60 * 1000;
// Retry transient renewal failures without creating a tight request loop.
const RENEWAL_RETRY_MS = 30 * 1000;

export class ShortLivedTokenManager {
    /**
     * Creates a token manager backed by the mapper API client.
     * @param {Object} api API client exposing apiGetShortLivedToken().
     * @param {Function} logger Application logger accepting severity, message, and details.
     */
    constructor(api, logger) {
        this.api = api;
        this.logger = logger;
        this.token = null;
        this.authorizationHeader = null;
        this.expiresAt = null;
        this.renewalTimer = null;
        this.expirationTimer = null;
        this.renewalRequest = null;
        this.changeListeners = new Set();
    }

    /**
     * Stores an issued token and schedules its renewal.
     * @param {{token: string, expiresAt: number}|null|undefined} issuedToken Token returned by mapper.
     * @returns {boolean} True when a valid token was stored; otherwise false.
     */
    start(issuedToken) {
        if (!this._isValid(issuedToken)) {
            this.stop();
            if (issuedToken != null) {
                this.logger("WARN", "Mapper returned an invalid short-lived token");
            }
            return false;
        }

        this._storeToken(issuedToken);
        return true;
    }

    /**
     * Returns the currently stored token string.
     * Expiration is handled by a dedicated timer, so this is a field lookup only.
     * @returns {string|null} Token value, or null when it is missing or expired.
     */
    getToken() {
        return this.token;
    }

    /**
     * Returns the prepared Authorization header value without allocations.
     * Expiration is handled by a dedicated timer, so this is a field lookup only.
     * @returns {string|null} The `Bearer <token>` value, or null without a valid token.
     */
    getAuthorizationHeader() {
        return this.authorizationHeader;
    }

    /**
     * Subscribes to changes of the prepared Authorization header value.
     * @param {Function} callback Listener receiving the new header string or null.
     * @returns {Function} Function that removes this listener.
     * @throws {TypeError} When callback is not a function.
     */
    onChange(callback) {
        if (typeof callback !== "function") {
            throw new TypeError("Short-lived token change listener must be a function");
        }
        this.changeListeners.add(callback);
        return () => this.changeListeners.delete(callback);
    }

    /**
     * Immediately requests a replacement token while deduplicating concurrent calls.
     * @returns {Promise<void>} Resolves after renewal or after a retry is scheduled.
     */
    async refresh() {
        return this._renew();
    }

    /**
     * Clears the stored credential and cancels its scheduled renewal.
     * @returns {void}
     */
    stop() {
        if (this.renewalTimer != null) {
            clearTimeout(this.renewalTimer);
        }
        this.renewalTimer = null;
        this._clearToken();
    }

    /**
     * Checks the mapper token response shape before storing sensitive data.
     * @param {Object|null|undefined} issuedToken Candidate token object.
     * @returns {boolean} True when the token and absolute expiration are usable.
     * @private
     */
    _isValid(issuedToken) {
        return issuedToken != null &&
            typeof issuedToken.token === "string" &&
            issuedToken.token.length > 0 &&
            Number.isFinite(issuedToken.expiresAt);
    }

    /**
     * Atomically stores all token representations and schedules lifecycle timers.
     * @param {{token: string, expiresAt: number}} issuedToken Valid mapper token object.
     * @returns {void}
     * @private
     */
    _storeToken(issuedToken) {
        this.token = issuedToken.token;
        this.authorizationHeader = `Bearer ${issuedToken.token}`;
        this.expiresAt = issuedToken.expiresAt;
        this._scheduleExpiration();
        this._scheduleRenewal();
        this._notifyChange();
    }

    /**
     * Clears every stored token representation and its expiration timer.
     * @returns {void}
     * @private
     */
    _clearToken() {
        if (this.expirationTimer != null) {
            clearTimeout(this.expirationTimer);
        }
        this.expirationTimer = null;
        const hadAuthorizationHeader = this.authorizationHeader != null;
        this.token = null;
        this.authorizationHeader = null;
        this.expiresAt = null;
        if (hadAuthorizationHeader) {
            this._notifyChange();
        }
    }

    /**
     * Clears the credential exactly when its server-provided lifetime ends.
     * @returns {void}
     * @private
     */
    _scheduleExpiration() {
        if (this.expirationTimer != null) {
            clearTimeout(this.expirationTimer);
        }
        const delay = Math.max(0, this.expiresAt * 1000 - Date.now());
        this.expirationTimer = setTimeout(() => this._clearToken(), delay);
    }

    /**
     * Notifies subscribers after the authorization value changes.
     * Listener errors are logged and do not interrupt token lifecycle handling.
     * @returns {void}
     * @private
     */
    _notifyChange() {
        for (const callback of this.changeListeners) {
            try {
                callback(this.authorizationHeader);
            } catch (error) {
                this.logger("ERROR", "Short-lived token change listener failed", error);
            }
        }
    }

    /**
     * Schedules renewal one minute before the current token expires.
     * @returns {void}
     * @private
     */
    _scheduleRenewal() {
        if (this.renewalTimer != null) {
            clearTimeout(this.renewalTimer);
        }
        const renewalAt = this.expiresAt * 1000 - RENEWAL_ADVANCE_MS;
        const delay = Math.max(0, renewalAt - Date.now());
        this.renewalTimer = setTimeout(() => this._renew(), delay);
    }

    /**
     * Requests and stores a replacement token, retrying transient failures.
     * Concurrent callers share the same renewal request.
     * @returns {Promise<void>} Resolves after renewal or after a retry is scheduled.
     * @private
     */
    async _renew() {
        if (this.renewalRequest != null) {
            return this.renewalRequest;
        }

        this.renewalTimer = null;
        this.renewalRequest = this.api.apiGetShortLivedToken()
            .then((response) => {
                if (!this._isValid(response.data)) {
                    throw new Error("Mapper returned an invalid short-lived token");
                }
                this._storeToken(response.data);
            })
            .catch((error) => {
                this.logger("WARN", "Failed to renew the short-lived token", error);
                this.renewalTimer = setTimeout(() => this._renew(), RENEWAL_RETRY_MS);
            })
            .finally(() => {
                this.renewalRequest = null;
            });

        return this.renewalRequest;
    }
}
